import { useTenantNavigate } from '@app-store'
import {
  ConfirmDialog,
  ControlledDropDown,
  ControlledMultiSelect,
  CustomAgTable,
  CustomButton,
  FormInput,
  Pagination,
  Popover,
  SORT_KEYS,
  Tooltip,
  ViewComponent,
} from '@ui-kit'
import {
  addToaster,
  createGroupQueueConfigAPI,
  createQueueApi,
  DataType,
  DataTypeStore,
  DropdownType,
  EntityLookupSearchCriteria,
  formatDataObject,
  getAssignedGroupsQueueConfigAPI,
  getCategoryDropdownData,
  getEntityDataByEntityIdLayoutIdApiForFilterQueue,
  getFilteredEntities,
  getLayoutByEntityIdTypeApi,
  getLayoutByIdApi,
  getPermissionSetsAPI,
  getProgramForQueueConfigAPI,
  getQueueByIdApi,
  hideLoader,
  IEntityFieldType,
  IEntityType,
  LookupRelatedEntities,
  LookupSearchCriteria,
  QueueCreation,
  SearchCriteriaByColumnNames,
  setQueueCreation,
  setSelectedQueueReducer,
  showLoader,
  TableFilterLookupObjectBody,
  TableFilterObjectBody,
  updateQueueApi,
} from '@app-store'
import { useForm } from 'react-hook-form'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { useEffect, useMemo, useRef, useState } from 'react'
import { EntityLayout } from '../../types/layout.types'
import { STRINGS } from '../../utils/strings'
import {
  componentNames,
  FIELD_VALUE_CONSTANTS,
  MODE_CONSTANTS,
  NAMESPACES,
  PAGINATION,
  REGEX_ERR_MSG,
  SORT_DIRECTION,
} from '../../utils/constants'
import {
  formatDataForListView,
  formatFilterArray,
  formatFilterConfig,
  getTableHeadersInQueueCreation,
} from '../../utils/listing.utils'
import { useLocation, useParams } from 'react-router-dom'
import { isEmpty, noop, uniqueId } from 'lodash'
import { convertToCamelCase } from '../../utils/helperFunctions'
import { useFocusFirstElement } from '../../hooks/useFocusFirstFocusableElement'
import { ShortcutKeys, TAB_INDEX_DEFAULT } from '../../constants/form.constants'
import { DropDownOption } from '../../types/types'

const AssignGroup = ({
  setOpenFieldModal,
  setIsNewAssignedGroup,
  assignedGroupOptions,
}: {
  setOpenFieldModal: React.Dispatch<React.SetStateAction<boolean>>
  setIsNewAssignedGroup: React.Dispatch<React.SetStateAction<boolean>>
  assignedGroupOptions: DropdownType<DataType, string>[]
}) => {
  const assignGroup: { group_id: string | null } = {
    group_id: null,
  }
  const dispatch = useAppDispatch()
  const {
    formState: { errors },
    register,
    setValue,
    handleSubmit,
  } = useForm({ defaultValues: assignGroup })

  const onSubmit = async (data: { group_id: string | null }) => {
    try {
      dispatch(showLoader())
      const response = await createGroupQueueConfigAPI({
        name: data?.group_id ?? '',
      })
      if (response) {
        setOpenFieldModal(false)
        setIsNewAssignedGroup(true)
        dispatch(
          addToaster({
            id: uniqueId(),
            message: 'Group created Successfully',
            type: 'success',
          })
        )
      }
      dispatch(hideLoader())
    } catch (err) {
      console.error('Err', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <FormInput
          dataQaId="form-input-queue-assign-group"
          name={'group_id'}
          label="Group Name"
          errors={errors}
          register={register}
          onChange={(e) =>
            setValue('group_id', e?.target?.value, { shouldValidate: true })
          }
          validation={{
            required: true,
            validate: (value) => {
              const trimmedValue = value?.trim()
              const isValid = assignedGroupOptions.find(
                (item) => item?.label === trimmedValue
              )
              const empty = trimmedValue?.length === 0

              if (empty) {
                return 'This is a required field'
              }

              if (isValid) {
                return 'Group name already exists'
              }

              return true
            },
          }}
        />

        <div className="justify-end mt-4 flex gap-4">
          <CustomButton
            dataQaId="custom-button-discard-group"
            color="others"
            isFilled={true}
            onClick={() => setOpenFieldModal(false)}
            className="text-accent-900 text-xs h-10 border-[0.075rem] border-primary-500"
            widthClass=""
          >
            {NAMESPACES.CANCEL_BUTTON}
          </CustomButton>

          <CustomButton
            dataQaId="custom-button-assign-group"
            color="primary"
            isFilled={true}
            type={'submit'}
            onClick={noop}
            className="text-accent-0 text-xs"
            widthClass=""
          >
            {NAMESPACES.SAVE_BUTTON}
          </CustomButton>
        </div>
      </div>
    </form>
  )
}

const CreateQueue = () => {
  const formStructure: QueueCreation = {
    name: null,
    description: null,
    entity_id: null,
    created_at: null,
    filter_layout_id: null,
    layout_id: null,
    queue_permissions_set: null,
    queue_group_id: {
      name: null,
      id: null,
    },
    queue_programs: null,
    assign_group_id: null,
    assign_group_name: null,
    filter_criteria: null,
    enabled: false,
  }

  const { tenantid, id } = useParams()
  const dispatch = useAppDispatch()
  const navigate = useTenantNavigate()
  const location = useLocation()
  const edit_entity_id = location?.state?.edit_entity_id
  const selectedClientName = useAppSelector(
    (state: RootState) =>
      state?.authUser?.selectedClientTenant?.name_of_organization
  )
  const categoryDropdownValues = useAppSelector(
    (state: RootState) => state?.categoryDropdown?.categoryMap
  )

  const {
    formState: { errors },
    control,
    register,
    setValue,
    handleSubmit,
    getValues,
  } = useForm({ defaultValues: formStructure })

  const [entityDropdownOptions, setEntityDropdownOptions] = useState<
    DropdownType<DataType, string>[]
  >([])

  const [layoutDropdownOptions, setLayoutDropdownOptions] = useState<
    DropdownType<DataType, string>[]
  >([])

  const [actionMode, setActionMode] = useState<string>(
    location?.state?.actionMode
  )

  const [isEntitySelected, setIsEntitySelected] = useState<boolean>(false)
  const [entityName, setEntityName] = useState<string>()
  const [queueData, setQueueData] = useState<QueueCreation | null>(null)
  const [listingLayout, setListingLayout] = useState<EntityLayout>()
  const [modal, setModal] = useState<{ type: string; state: boolean }>({
    type: '',
    state: false,
  })
  const [totalPages, setTotalPages] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [currentPage, setCurrentPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [queueConfig, setQueueConfig] = useState<{
    entityName: string
    layoutName: string
    filteringLayoutName: string
    programNames: string[]
    permission_set_names: string[]
    assignedGroup: string
  }>()
  const [apiFilter, setApiFilter] = useState<TableFilterObjectBody>({
    fields: null,
    search_criteria: {},
  })
  const [rowPerPage, setRowPerPage] = useState<number>(PAGINATION.ROWS_PER_PAGE)
  const [entityData, setEntityData] = useState<DataTypeStore[]>([])
  const [prevEntityId, setPrevEntityId] = useState<string>()
  const [prevFilteringLayoutId, setPrevFilteringLayoutId] = useState<string>()
  const hasMounted = useRef<boolean>(false)

  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  }>({
    keyName: '',
    direction: SORT_KEYS.Disabled,
    isAsc: SORT_KEYS.Disabled,
    key: '',
  })

  const [sortClick, setSortClick] = useState<boolean>(false)
  const [isEntityOnChange, setIsEntityOnChange] = useState<{
    mode: string | null
    toggle: boolean
  }>({
    mode: null,
    toggle: false,
  })
  const [isDisplayLayoutSelected, setIsDisplayLayoutSelected] =
    useState<boolean>(false)

  const [permissionSetOptions, setPermissionSetOptions] = useState<
    DropdownType<DataType, string>[]
  >([])
  const [programConfigOptions, setProgramConfigOptions] = useState<
    DropdownType<DataType, string>[]
  >([])

  const [assignedGroupOptions, setAssignedGroupOptions] = useState<
    DropdownType<DataType, string>[]
  >([])

  const [openFieldModal, setOpenFieldModal] = useState<boolean>(false)
  const [programConf, setProgramConf] = useState<string[]>([])

  const [isNewAssignedGroup, setIsNewAssignedGroup] = useState<boolean>(true)
  const [coordinates, setCoordinates] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const spanRef = useRef<HTMLSpanElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [entityProgramConfig, setEntityProgramConfig] =
    useState<string>('programs')
  useFocusFirstElement(containerRef)

  const columnConfig = useMemo(() => {
    return (
      getTableHeadersInQueueCreation(listingLayout, undefined, [
        {
          key: entityProgramConfig,
          disabled: !!programConf?.length,
        },
      ]) ?? []
    )
  }, [
    entityProgramConfig,
    programConf,
    listingLayout,
    queueData,
    categoryDropdownValues,
  ])

  const fetchCategoryDropdowns = () => {
    for (const key in listingLayout?.layout_data?.added_fields ?? {}) {
      const field: IEntityFieldType & {
        filter_criteria?: { category_type?: string }
      } = listingLayout?.layout_data?.added_fields?.[key] ?? {}
      if (field.ui_data_type === componentNames.CATEGORY)
        dispatch(getCategoryDropdownData(field.filter_criteria?.category_type))
    }
  }

  const fetchAllEntities = async () => {
    try {
      dispatch(showLoader())
      const payload = {
        fields: null,
        search_criteria: [
          {
            search_key: NAMESPACES.QUEUE_VISIBILITY,
            search_value: [true],
            operator: NAMESPACES.EQUALS_BOOLEAN,
          },
        ],
      }
      const response = await getFilteredEntities(payload)
      if (response) {
        const dropdownOptions: DropdownType<DataType, string>[] =
          response?.data?.content
            ?.map((item: IEntityType) => ({
              label: item?.name ?? '',
              value: item?.id.toString() ?? '-1',
            }))
            ?.sort((a: DropDownOption, b: DropDownOption) =>
              a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            )
        setEntityDropdownOptions(dropdownOptions)
      }
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleEntityChange = async (id: string, fromOnChange = false) => {
    try {
      if (id === prevEntityId) {
        return
      }
      dispatch(showLoader())
      const response = await getLayoutByEntityIdTypeApi({
        type: 'LISTING_MODE',
        entityId: id,
      })
      if (response) {
        const dropdownOptions: DropdownType<DataType, string>[] = response
          ?.map((item: EntityLayout) => ({
            label: item?.name ?? '',
            value: item?.id?.toString() ?? '-1',
            default_value: item?.default_value,
          }))
          ?.sort((a: DropDownOption, b: DropDownOption) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          )

        if (hasMounted.current) {
          setApiFilter({ fields: null, search_criteria: {} })
        } else {
          hasMounted.current = true
        }

        setIsEntitySelected(true)
        setIsDisplayLayoutSelected(false)
        setLayoutDropdownOptions(dropdownOptions)
        setPrevEntityId(id)
        setValue('filter_layout_id', null)
        setValue('layout_id', null)

        if (fromOnChange) {
          setIsEntityOnChange({
            mode: actionMode,
            toggle: true,
          })
        }
      }
      dispatch(hideLoader())
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uniqueId(),
          message: 'No listing layout for this entity',
          type: 'error',
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchLayoutById = async (entity_id: string, layout_id: string) => {
    try {
      dispatch(showLoader())
      const response = await getLayoutByIdApi({
        entityId: entity_id,
        layoutId: layout_id,
      })
      if (response) {
        setValue('filter_layout_id', layout_id)
        setListingLayout(response?.data)
        setEntityName(response?.data?.entity_name)
        setIsDisplayLayoutSelected(true)
      }
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onFilterChange = (value: TableFilterObjectBody) => {
    setApiFilter(value)
  }

  const getKeys = () => {
    const keys = Object.keys(listingLayout?.layout_data?.added_fields ?? {})
    if (!keys?.includes('id')) {
      keys.unshift('id')
    }
    return keys
  }

  const enableSorting = (
    filters: { logical_operator?: string; criteria: formatDataObject[] }[]
  ) => {
    const findSortingCrit = queueData?.filter_criteria?.find(
      (item) => item?.sort_direction === SORT_DIRECTION?.desc?.upperCase
    )
    if (findSortingCrit) {
      filters?.push({
        criteria: [
          {
            direction: findSortingCrit?.sort_direction,
            field_name: findSortingCrit?.field_name,
          },
        ],
      })
    }
  }

  const updateProgramNameCriteria = (
    filters: {
      logical_operator?: string
      criteria: formatDataObject[]
    }[],
    newValues: string[] | null | undefined = null
  ) => {
    filters.forEach((filter) => {
      filter.criteria.forEach((criterion) => {
        if (criterion.field_name === 'operational_program_name') {
          if (newValues) {
            criterion.values = [...newValues]
          } else if (criterion.values) {
            criterion.values = [...criterion.values]
          } else {
            criterion.values = []
          }
        }
      })
    })
  }

  const transformCriteria = (criteria: LookupRelatedEntities[] = []) => {
    return criteria.map((criterion) => ({
      field_name: criterion.field_name,
      operation: criterion.operation,
      values: criterion.values,
      direction: criterion.direction ?? null,
    }))
  }

  const fetchEntityData = async (
    rowsPerPage?: number,
    page = 0,
    initialDataLoad = true
  ) => {
    try {
      dispatch(showLoader())
      const keys = getKeys()

      if (initialDataLoad) setCurrentPage(1)

      const related_entities: Record<string, { select: string[] }> = {}
      const sortingCriteria = {
        criteria: [
          {
            direction:
              sortDirection?.direction === SORT_DIRECTION.desc.lowerCase
                ? SORT_DIRECTION?.desc?.upperCase
                : SORT_DIRECTION.asc.upperCase,
            field_name: sortDirection?.key,
          },
        ],
      }

      let filters: {
        logical_operator?: string
        criteria: formatDataObject[]
      }[] = [
        formatFilterArray({
          fields: null,
          search_criteria: apiFilter.search_criteria,
        }),
      ]

      if (queueData && !sortClick) {
        enableSorting(filters)
      }

      if (sortClick) {
        filters = filters?.filter(
          (item) => JSON.stringify(item) !== JSON.stringify(sortingCriteria)
        )
        filters?.push(sortingCriteria)
      }

      Object.entries(listingLayout?.layout_data?.added_fields ?? {}).forEach(
        ([fieldName, field]) => {
          if (field?.ui_data_type !== componentNames.SINGLE_LOOKUP) return
          related_entities[fieldName] = {
            select: Object.keys(field.added_fields ?? {}),
          }
        }
      )

      const filterObj = formatFilterConfig(apiFilter, columnConfig, 'Entity', {
        key: sortDirection?.key,
        direction: sortDirection?.direction?.toLocaleUpperCase(),
      })
      const filterConfig = {
        filters: filterObj?.filter_criteria as EntityLookupSearchCriteria[],
        related_entities: filterObj?.related_entities,
      }
      if (!filterConfig.related_entities[`${entityProgramConfig}`]) {
        filterConfig.related_entities[`${entityProgramConfig}`] = {
          filters: [],
        }
      }

      const targetFilters =
        filterConfig.related_entities[entityProgramConfig].filters
      const hasExistingFilters = targetFilters.length > 0

      if (programConf?.length > 0) {
        if (hasExistingFilters) {
          updateProgramNameCriteria(targetFilters, programConf)
        } else {
          targetFilters.push({
            criteria: [
              {
                field_name: 'operational_program_name',
                values: programConf.slice(),
                operation: 'LIKE',
              },
            ],
          })
        }
      } else if (hasExistingFilters) {
        updateProgramNameCriteria(targetFilters)
      }

      if (!isEmpty(entityName)) {
        const entity = await getEntityDataByEntityIdLayoutIdApiForFilterQueue({
          payload: {
            entity_id: getValues('entity_id') ?? '-1',
            select: keys || [],
            page,
            related_entities: filterConfig?.related_entities,
            size: rowsPerPage ?? rowPerPage,
            filters: filterConfig?.filters,
          },
          entityName: entityName ?? '',
        })
        setEntityData(
          formatDataForListView(
            entity?.data?.content,
            listingLayout?.layout_data,
            true
          )
        )

        if (entity) {
          setTotalPages(entity?.data?.totalPages)
          setIsDisplayLayoutSelected(true)
        }
      }
    } catch (err) {
      console.error(err)
      setEntityData([])
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleLayoutChange = (id: string) => {
    if (id === prevFilteringLayoutId) {
      return
    }
    setValue('filter_layout_id', id, { shouldValidate: true })
    setApiFilter({ fields: null, search_criteria: {} })
    const entity_id = getValues('entity_id')
    fetchLayoutById(entity_id?.toString() ?? '-1', id)
    setPrevFilteringLayoutId(id)
    setIsDisplayLayoutSelected(true)
  }

  const handleDisplayLayoutChange = (id: string) => {
    setValue('layout_id', id, { shouldValidate: true })
  }

  const fetchQueueById = async (id: number) => {
    try {
      dispatch(showLoader())
      const response = await getQueueByIdApi(id)
      if (response) {
        const transformedRelatedEntities: TableFilterLookupObjectBody =
          Object.keys(response?.data?.related_entities ?? {}).reduce(
            (acc, key) => {
              const entity = response.data.related_entities[key]
              acc[key] = {
                filters: (entity.filters ?? []).map(
                  (filter: EntityLookupSearchCriteria) => ({
                    criteria: transformCriteria(filter.criteria),
                  })
                ),
              }
              return acc
            },
            {} as TableFilterLookupObjectBody
          )

        const transformedData = {
          ...response?.data,
          related_entities: transformedRelatedEntities,
        }

        setQueueData(transformedData)

        if (id) {
          setValue('filter_layout_id', transformedData?.name, {
            shouldValidate: true,
          })
          dispatch(setSelectedQueueReducer(transformedData?.name))
        }
      }
    } catch (err) {
      console.error('Error', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handlePageChange = async (page: number, rowsPerPage: number) => {
    try {
      dispatch(showLoader())
      setRowPerPage(rowsPerPage)
      const currentPage = rowsPerPage !== rowPerPage || page === 1 ? 1 : page
      setCurrentPage(currentPage)
      await fetchEntityData(rowsPerPage, currentPage - 1, false)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const createPayload = (
    data: QueueCreation,
    transformedFilter: {
      filter_criteria: LookupSearchCriteria[] | EntityLookupSearchCriteria[]
      related_entities: TableFilterLookupObjectBody
    }
  ) => {
    let status = 'Active'
    if (id) {
      if (!queueData?.enabled) {
        status = 'Inactive'
      }
    }

    const payload = {
      ...data,
      enabled: id ? queueData?.enabled : true,
      status,
      deleted: false,
      deleted_timestamps: null,
      filter_criteria: transformedFilter?.filter_criteria,
      related_entities: transformedFilter?.related_entities,
      queue_group_id:
        getValues('assign_group_id') && getValues('assign_group_name')
          ? {
              id: getValues('assign_group_id'),
              name: getValues('assign_group_name'),
            }
          : null,
    }

    return id
      ? {
          ...payload,
          id: Number(id),
          created_at: queueData?.created_at,
        }
      : payload
  }

  const handleApiError = (err: unknown) => {
    console.error('Error', err)
    dispatch(
      addToaster({
        id: uniqueId(),
        message: `${
          (err as { response: { data: { message: string } } })?.response?.data
            ?.message
        }. ${
          (
            err as { response: { data: { message: string } } }
          )?.response?.data?.message?.includes('exists')
            ? STRINGS.ERROR_DIFF_NAME
            : ''
        }`,
        type: 'error',
      })
    )
  }

  const onSubmit = async (data: QueueCreation) => {
    try {
      dispatch(showLoader())
      const filterConfig = formatFilterConfig(
        apiFilter,
        columnConfig,
        'Queue',
        {
          key: sortDirection?.key,
          direction: sortDirection?.direction?.toLocaleUpperCase(),
        }
      )

      const payload = createPayload(data, filterConfig)

      const response = id
        ? await updateQueueApi(payload)
        : await createQueueApi(payload)

      if (response?.data) {
        dispatch(
          setQueueCreation({
            queueData: response?.data,
            type: id ? 'update' : 'post',
          })
        )
        dispatch(
          addToaster({
            id: uniqueId(),
            message: id
              ? STRINGS.QUEUE_UPDATION_TOAST
              : STRINGS.QUEUE_CREATION_TOAST,
            type: 'success',
          })
        )
        navigate('/manage-queue')
      }
    } catch (err) {
      handleApiError(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleQueueTitle = () => {
    switch (actionMode) {
      case MODE_CONSTANTS.VIEW:
        return NAMESPACES.VIEW_QUEUE
      case MODE_CONSTANTS.EDIT:
        return NAMESPACES.EDIT_QUEUE
      case MODE_CONSTANTS.ADD:
        return NAMESPACES.QUEUE_CREATION
    }
  }

  const getInitialFilterData = (
    filter_criteria?:
      | {
          field_name: string
          operation: string
          sort_direction: string
          values: string[]
        }[]
      | null,
    related_entities?: TableFilterLookupObjectBody
  ) => {
    const filterData = {} as SearchCriteriaByColumnNames

    filter_criteria?.forEach((filter) => {
      filterData[filter.field_name] = {
        operator: filter.operation,
        search_key: filter.field_name,
        search_value: filter.values,
      }

      if (filter?.sort_direction) {
        setSortDirection({
          key: filter.field_name,
          direction: filter.sort_direction.toLowerCase() as SORT_KEYS,
          keyName: filter.field_name,
          isAsc: filter.sort_direction.toLowerCase() as SORT_KEYS,
        })
      }
    })

    if (related_entities) {
      Object.keys(related_entities).forEach((key) => {
        const entity = related_entities[key]
        const values = entity?.filters?.[0]?.criteria?.[0]?.values || []
        const direction = entity?.filters?.[0]?.criteria?.[0]?.direction

        filterData[key] = {
          operator: entity?.filters?.[0]?.criteria?.[0]?.operation ?? 'LIKE',
          search_key: key,
          search_value: values,
        }

        if (direction) {
          setSortDirection({
            key: key,
            direction: direction.toLowerCase() as SORT_KEYS,
            keyName: key,
            isAsc: direction.toLowerCase() as SORT_KEYS,
          })
        }
      })
    }

    return filterData
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      setSortClick(true)
      setSortDirection(() => {
        const newKeyName = convertToCamelCase(key)
        return {
          keyName: isAsc === SORT_KEYS?.Disabled ? '' : newKeyName,
          direction: isAsc,
          key: isAsc === SORT_KEYS?.Disabled ? '' : key,
          isAsc: isAsc,
        }
      })
    }
  }

  const fetchPermissionSets = async () => {
    try {
      dispatch(showLoader())
      const response = await getPermissionSetsAPI()
      if (response) {
        const dropdownOptions: DropdownType<DataType, string>[] = response?.data
          ?.filter(
            (item: { permission_set_name: string; id: number }) =>
              item.permission_set_name !== 'Program Admin' &&
              item.permission_set_name !== 'System Admin'
          )
          .map((item: { permission_set_name: string; id: number }) => ({
            label: item?.permission_set_name ?? '',
            value: item?.id?.toString() ?? '-1',
          }))

        setPermissionSetOptions(dropdownOptions)
      }

      dispatch(hideLoader())
    } catch (err) {
      console.error('Err: ', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const getButtonText = () => {
    return id ? NAMESPACES.UPDATE_BUTTON : NAMESPACES.SAVE_BUTTON
  }

  const fetchProgramsQueueConfig = async () => {
    try {
      dispatch(showLoader())
      const response = await getProgramForQueueConfigAPI()
      if (response) {
        const dropdownOptions: DropdownType<DataType, string>[] =
          response?.data?.content?.map(
            (item: { operational_program_name: string; id: number }) => ({
              label: item?.operational_program_name ?? '',
              value: item?.id?.toString() ?? '-1',
            })
          )
        setProgramConfigOptions(dropdownOptions)
      }

      dispatch(hideLoader())
    } catch (err) {
      console.error('Err: ', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchAssignedGroupsQueue = async () => {
    try {
      dispatch(showLoader())
      const response = await getAssignedGroupsQueueConfigAPI()
      if (response) {
        const dropdownOptions: DropdownType<DataType, string>[] =
          response?.data?.content
            ?.map((item: { name: string; id: number }) => ({
              label: item?.name ?? '',
              value: item?.id?.toString() ?? '-1',
            }))
            ?.sort((a: DropDownOption, b: DropDownOption) =>
              a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            )
        setAssignedGroupOptions(dropdownOptions)
        setIsNewAssignedGroup(false)
      }

      dispatch(hideLoader())
    } catch (err) {
      console.error('Err: ', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchDefaultListView = async () => {
    try {
      const defaultListView = layoutDropdownOptions.find(
        (item) => item?.default_value
      )

      fetchLayoutById(
        getValues('entity_id') ?? '',
        (defaultListView?.value ?? '')?.toString()
      )
    } catch (err) {
      console.error('Err :', err)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    fetchAllEntities()
    fetchPermissionSets()
    fetchProgramsQueueConfig()
    setSortClick(false)
  }, [])

  useEffect(() => {
    if (
      isEntityOnChange.mode === MODE_CONSTANTS.EDIT &&
      isEntityOnChange.toggle
    ) {
      fetchDefaultListView()
      setIsEntityOnChange({
        mode: null,
        toggle: false,
      })
    }
  }, [isEntityOnChange])

  useEffect(() => {
    if (programConf && listingLayout) {
      fetchEntityData()
    }
  }, [programConf, listingLayout])

  useEffect(() => {
    if (
      actionMode !== MODE_CONSTANTS.EDIT &&
      isEntitySelected &&
      !isDisplayLayoutSelected &&
      layoutDropdownOptions?.length > 0
    ) {
      fetchDefaultListView()
    }
  }, [
    isDisplayLayoutSelected,
    isEntitySelected,
    layoutDropdownOptions,
    actionMode,
  ])

  useEffect(() => {
    if (isNewAssignedGroup) {
      fetchAssignedGroupsQueue()
    }
  }, [isNewAssignedGroup])

  useEffect(() => {
    if (id) {
      fetchQueueById(Number(id))
    }
    if (id && edit_entity_id) {
      handleEntityChange(edit_entity_id)
    }
  }, [id, edit_entity_id])

  useEffect(() => {
    if (
      listingLayout &&
      isDisplayLayoutSelected &&
      actionMode !== MODE_CONSTANTS.VIEW
    )
      fetchEntityData()
  }, [
    listingLayout,
    apiFilter,
    sortDirection,
    actionMode,
    isDisplayLayoutSelected,
  ])
  useEffect(() => {
    if (listingLayout) fetchCategoryDropdowns()
  }, [listingLayout])

  useEffect(() => {
    if (queueData && entityDropdownOptions && layoutDropdownOptions) {
      setValue(
        'filter_layout_id',
        (queueData?.filter_layout_id ?? '')?.toString()
      )
      setPrevFilteringLayoutId((queueData?.filter_layout_id ?? '')?.toString())
      setValue('layout_id', (queueData?.layout_id ?? '')?.toString())
    }
  }, [queueData, entityDropdownOptions, layoutDropdownOptions])

  useEffect(() => {
    if (queueData && entityDropdownOptions && layoutDropdownOptions) {
      setValue(
        'filter_layout_id',
        (queueData?.filter_layout_id ?? '')?.toString()
      )
      setValue('layout_id', (queueData?.layout_id ?? '')?.toString())
    }
  }, [queueData, entityDropdownOptions, layoutDropdownOptions])

  useEffect(() => {
    if (queueData && entityDropdownOptions && layoutDropdownOptions) {
      setValue(
        'filter_layout_id',
        (queueData?.filter_layout_id ?? '')?.toString()
      )
      setValue('layout_id', (queueData?.layout_id ?? '')?.toString())
    }

    if (queueData && programConfigOptions) {
      setValue(
        'queue_programs',
        (queueData?.queue_programs ?? [])?.map((item: string) =>
          item?.toString()
        )
      )

      const matchingLabels = programConfigOptions
        .filter(
          (program) =>
            (queueData?.queue_programs ?? []).filter(
              (queueProgram) =>
                queueProgram.toString() === program.value.toString()
            )?.length
        )
        .map((program) => program.label)

      setProgramConf(matchingLabels)
    }

    if (queueData && assignedGroupOptions) {
      setValue(
        'assign_group_id',
        (queueData?.queue_group_id?.id ?? '')?.toString()
      )
      setValue(
        'assign_group_name',
        (queueData?.queue_group_id?.name ?? '')?.toString()
      )
    }
    if (queueData && permissionSetOptions) {
      setValue(
        'queue_permissions_set',
        (queueData?.queue_permissions_set ?? [])?.map((item: string) =>
          item?.toString()
        )
      )
    }
    if (entityDropdownOptions) {
      setValue('entity_id', queueData?.entity_id?.toString() ?? '')
      setPrevEntityId(queueData?.entity_id?.toString() ?? '')
      setEntityProgramConfig(
        queueData?.entity_name === 'hcp_portal_entity'
          ? 'hcp_program'
          : 'programs'
      )
    }

    setValue('created_at', queueData?.created_at ?? '')

    setValue('name', queueData?.name ?? '')
    setValue('description', queueData?.description ?? '')
    if (getValues('filter_layout_id') && getValues('entity_id')) {
      fetchLayoutById(
        getValues('entity_id') ?? '',
        getValues('filter_layout_id') ?? ''
      )
    }

    const sortData = queueData?.filter_criteria?.find(
      (item) => item?.sort_direction === SORT_DIRECTION?.desc?.upperCase
    )

    const isAscending = (sortDirection: string) => {
      return sortDirection === SORT_DIRECTION?.asc?.upperCase
        ? SORT_KEYS.Asc
        : SORT_KEYS.Desc
    }

    if (sortData) {
      setSortDirection({
        key: sortData?.field_name,
        direction: sortData?.sort_direction?.toLowerCase() as SORT_KEYS,
        isAsc: isAscending(sortData?.sort_direction),

        keyName: sortData?.field_name,
      })
    }
    setApiFilter({
      fields: null,
      search_criteria: getInitialFilterData(
        queueData?.filter_criteria,
        queueData?.related_entities
      ),
    })
  }, [
    queueData,
    entityDropdownOptions,
    programConfigOptions,
    permissionSetOptions,
  ])

  useEffect(() => {
    if (queueData && entityDropdownOptions && layoutDropdownOptions) {
      const selectedLayoutLabel = layoutDropdownOptions.find(
        (option) =>
          option?.value?.toString() === queueData?.layout_id?.toString()
      )?.label

      const selectedFilteringLayoutLabel = layoutDropdownOptions.find(
        (option) =>
          option?.value?.toString() === queueData?.filter_layout_id?.toString()
      )?.label

      const selectedEntityLabel = entityDropdownOptions.find(
        (option) => option.value === queueData?.entity_id?.toString()
      )?.label

      const selectedProgramLabels = programConfigOptions
        .filter((option) =>
          queueData?.queue_programs?.map(Number).includes(Number(option.value))
        )
        .map((option) => option.label)

      const selectedPermissionSetLabels = permissionSetOptions
        .filter((option) =>
          queueData?.queue_permissions_set
            ?.map(Number)
            .includes(Number(option.value))
        )
        .map((option) => option.label)

      const selectedAssignedGroupLabel =
        assignedGroupOptions.find(
          (option) =>
            option?.value?.toString() ===
            queueData?.queue_group_id?.id?.toString()
        )?.label ?? ''

      setQueueConfig({
        ...queueConfig,
        entityName: selectedEntityLabel ?? '',
        layoutName: selectedLayoutLabel ?? '',
        filteringLayoutName: selectedFilteringLayoutLabel ?? '',
        programNames: selectedProgramLabels,
        permission_set_names: selectedPermissionSetLabels,
        assignedGroup: selectedAssignedGroupLabel,
      })
    }
  }, [
    queueData,
    layoutDropdownOptions,
    entityDropdownOptions,
    programConfigOptions,
    permissionSetOptions,
    assignedGroupOptions,
  ])

  return (
    <div
      className="relative font-primary flex flex-col gap-2 h-full"
      ref={containerRef}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col">
          <div className="flex grid-cols-2 justify-between px-3 py-2 border-b-2 items-center">
            <h2 className="text-base col-span-1 font-primary font-medium">
              {handleQueueTitle()}
            </h2>
            <div className="flex justify-between items-center gap-4">
              <CustomButton
                dataQaId="custom-button-discard-queue-creation"
                color="others"
                isFilled={true}
                onClick={() => {
                  if (actionMode !== MODE_CONSTANTS.VIEW) {
                    setModal({
                      type: '',
                      state: true,
                    })
                  } else {
                    navigate(`/manage-queue`)
                  }
                }}
                className="text-accent-900 text-xs h-10 border-[0.075rem] border-primary-500"
                widthClass=""
              >
                {actionMode === MODE_CONSTANTS.VIEW
                  ? NAMESPACES.BACK_BUTTON
                  : NAMESPACES.CANCEL_BUTTON}
              </CustomButton>
              <div>
                {actionMode === MODE_CONSTANTS.VIEW ? (
                  <div>
                    <CustomButton
                      dataQaId="custom-button-save-queue-creation"
                      color="primary"
                      isFilled={true}
                      disabled={queueData?.enabled === false}
                      onClick={() => {
                        navigate(`/manage-queue/${tenantid}/edit-queue/${id}`, {
                          state: {
                            actionMode: 'edit',
                            edit_entity_id: edit_entity_id,
                          },
                        })
                        setActionMode(MODE_CONSTANTS.EDIT)
                      }}
                      type="button"
                      className="text-accent-0 text-xs"
                      widthClass=""
                    >
                      {NAMESPACES.EDIT_BUTTON}
                    </CustomButton>
                  </div>
                ) : (
                  <CustomButton
                    dataQaId="custom-button-save-queue-creation"
                    color="primary"
                    isFilled={true}
                    type={'submit'}
                    className="text-accent-0 text-xs"
                    disabled={queueData?.enabled === false}
                    widthClass=""
                  >
                    {getButtonText()}
                  </CustomButton>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 p-3">
          <div className="col-span-1">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <ViewComponent label={'Queue Name'} data={queueData?.name} />
            ) : (
              <FormInput
                dataQaId="form-input-queue-name"
                name={'name'}
                label="Queue Name"
                errors={errors}
                register={register}
                onChange={(e) =>
                  setValue('name', e?.target?.value, { shouldValidate: true })
                }
                validation={{
                  required: true,
                  maxLength: {
                    value: 50,
                    message: REGEX_ERR_MSG.FIFTY,
                  },
                }}
              />
            )}
          </div>
          <div className="col-span-2">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <div className="lg:min-w-[17rem]">
                <Tooltip
                  content={queueData?.description ?? ''}
                  position="bottom"
                  className="tooltip-text-wrap"
                >
                  <ViewComponent
                    label={'Description'}
                    data={queueData?.description}
                  />
                </Tooltip>
              </div>
            ) : (
              <FormInput
                dataQaId="form-input-description-create-queue"
                name={'description'}
                label="Description"
                register={register}
                onChange={(e) =>
                  setValue('description', e?.target?.value, {
                    shouldValidate: true,
                  })
                }
                errors={errors}
                validation={{
                  required: true,
                  maxLength: {
                    value: FIELD_VALUE_CONSTANTS.TWO_HUNDRED_FIFTY,
                    message: REGEX_ERR_MSG.TWO_HUNDRED_FIFTY,
                  },
                }}
              />
            )}
          </div>
          <div className="col-span-1">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <ViewComponent label={'Entity'} data={queueConfig?.entityName} />
            ) : (
              <ControlledDropDown
                dataQaId="controlled-dropdown-entities-list"
                name="entity_id"
                label="Entity"
                placeholder=""
                onChange={(e) => {
                  handleEntityChange(e?.value, true)
                  setValue('entity_id', e?.value?.toString(), {
                    shouldValidate: true,
                  })
                  setEntityProgramConfig(
                    e?.label === 'hcp_portal_entity'
                      ? 'hcp_program'
                      : 'programs'
                  )
                }}
                options={entityDropdownOptions}
                control={control}
                errors={errors}
                required={true}
                isSearchable
              />
            )}
          </div>
          <div className="col-span-1">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <ViewComponent
                label={'Filtering Layout'}
                data={queueConfig?.filteringLayoutName}
              />
            ) : (
              <ControlledDropDown
                dataQaId="controlled-dropdown-filtering-layout-options"
                name="filter_layout_id"
                label="Filtering Layout"
                placeholder=""
                onChange={(e) => handleLayoutChange(e?.value)}
                options={layoutDropdownOptions}
                disabled={
                  isEntitySelected === false && !getValues('filter_layout_id')
                }
                control={control}
                errors={errors}
                required={true}
                isSearchable
              />
            )}
          </div>
          <div className="col-span-1">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <ViewComponent
                label={'Display Layout'}
                data={queueConfig?.layoutName}
              />
            ) : (
              <ControlledDropDown
                dataQaId="controlled-dropdown-display-layout-options"
                name="layout_id"
                label="Display Layout"
                placeholder=""
                onChange={(e) => handleDisplayLayoutChange(e?.value)}
                options={layoutDropdownOptions}
                disabled={isEntitySelected === false && !getValues('layout_id')}
                control={control}
                errors={errors}
                required={true}
                isSearchable
              />
            )}
          </div>
          {selectedClientName !== 'Global' && (
            <div className="col-span-1">
              {actionMode === MODE_CONSTANTS.VIEW ? (
                <ViewComponent
                  label={'Program'}
                  data={queueConfig?.programNames}
                />
              ) : (
                <div>
                  <ControlledMultiSelect
                    dataQaId="multiselect-programs-dropdown-queue"
                    name="queue_programs"
                    label={'Program'}
                    control={control}
                    options={programConfigOptions}
                    isSearchable
                    errors={errors}
                    noOfChips={2}
                    chipDataWidth="max-w-20"
                    onChange={(value: DropdownType<string>[]) => {
                      const options: string[] = []
                      const labels: string[] = []
                      value?.forEach((item) => {
                        options.push(item?.value?.toString())
                        labels.push(item?.label?.toString())
                      })
                      setValue('queue_programs', options, {
                        shouldValidate: true,
                      })
                      setProgramConf(labels)
                      setApiFilter((prev) => {
                        const prevState = {
                          ...prev,
                          search_criteria: { ...prev?.search_criteria },
                        }
                        delete prevState.search_criteria[entityProgramConfig]
                        return prevState
                      })
                    }}
                    popupClassname={'add-client-dropdown'}
                  />
                  <span className="text-xs font-light">
                    {NAMESPACES.CREATE_QUEUE_PROGRAM_CONFIG}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="col-span-1">
            {actionMode === MODE_CONSTANTS.VIEW ? (
              <ViewComponent
                label={'Assign Group'}
                data={queueConfig?.assignedGroup}
              />
            ) : (
              <div>
                <ControlledDropDown
                  dataQaId="controlled-dropdown-assign-group-options"
                  name="assign_group_id"
                  label="Assign group"
                  placeholder=""
                  onChange={(e) => {
                    setValue('assign_group_id', e?.value, {
                      shouldValidate: true,
                    })
                    setValue('assign_group_name', e?.label)
                  }}
                  isCrossable
                  options={assignedGroupOptions}
                  control={control}
                  errors={errors}
                  isSearchable
                />
                <div>
                  <span
                    className="cursor-pointer text-xs font-light text-secondary-500"
                    tabIndex={TAB_INDEX_DEFAULT}
                    ref={spanRef}
                    onKeyDown={(e) => {
                      if (
                        e.key === ShortcutKeys.Enter ||
                        e.key === ShortcutKeys.Space
                      ) {
                        const rect = spanRef.current?.getBoundingClientRect()
                        if (rect) {
                          setCoordinates({
                            x:
                              selectedClientName !== 'Global' ? rect.left : 900,
                            y: 210,
                          })
                        }
                        setOpenFieldModal(true)
                        e.preventDefault()
                      }
                    }}
                    onClick={(e) => {
                      setCoordinates({
                        x: selectedClientName !== 'Global' ? e?.clientX : 900,
                        y: 210,
                      })
                      setOpenFieldModal(true)
                    }}
                  >
                    {NAMESPACES.CREATE_QUEUE_ASSIGN_NEW_GROUP}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      <ConfirmDialog
        showCloseButtton={true}
        open={modal.state}
        confirmButtonText={NAMESPACES.YES}
        cancelButtonText={NAMESPACES.CANCEL_BUTTON}
        message={NAMESPACES.CANCEL_CHANGE_MSG}
        onConfirm={() => {
          navigate(`/manage-queue`)
          setModal({ type: '', state: false })
        }}
        onCancel={() => setModal({ type: '', state: false })}
        confirmButtonWidth="w-fit"
        width="38.5rem"
      />

      <div
        className="absolute right-1/4 flex justify-around"
        style={{
          top: coordinates?.y ? `${(coordinates.y + 16) / 16}rem` : 'auto',
          right: coordinates?.x ? `${(coordinates.x + 160) / 16}rem` : 'auto',
        }}
      >
        {openFieldModal && (
          <Popover
            isOpen={openFieldModal}
            onClose={() => setOpenFieldModal(false)}
            content={
              <AssignGroup
                setOpenFieldModal={setOpenFieldModal}
                setIsNewAssignedGroup={setIsNewAssignedGroup}
                assignedGroupOptions={assignedGroupOptions}
              />
            }
            cssClassName="min-w-[17.5rem] min-h-[10rem] max-h-[25rem] overflow-y-scroll hide-scrollbar"
            title={'Create New Group'}
            position="unset"
          />
        )}
      </div>

      {actionMode !== MODE_CONSTANTS.VIEW && isDisplayLayoutSelected && (
        <div className="flex flex-col h-full flex-1 min-h-0">
          <div className="font-primary font-light text-xs px-3">
            {NAMESPACES.CREATE_QUEUE_TABLE_DESCRIPTION}
          </div>
          <div className="flex flex-col flex-1 h-full min-h-0">
            <div className="mt-2 flex flex-col flex-1 min-h-0">
              <CustomAgTable<DataTypeStore>
                pageName="manage-queue-edit-page"
                tableName={`manage-queue-edit-table-${id}-${tenantid}`}
                columns={columnConfig}
                emptyDataMessage="No records found"
                data={entityData}
                onSortClick={onSortClick}
                showTableColumnOnEmpty
                sort={
                  sortDirection.key && !isEmpty(sortDirection?.key)
                    ? {
                        key: sortDirection?.key,
                        isAsc: sortDirection?.isAsc,
                      }
                    : null
                }
                onFilterChange={onFilterChange}
                filterObject={apiFilter}
              />
              {entityData.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreateQueue
